import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtGHS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Hospital, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { CreditBar } from "./common";
import { printSummary, csvSummary } from "./summary";
import { HospitalFormDialog } from "./HospitalFormDialog";
import { HospitalDetailDialog } from "./HospitalDetailDialog";

export const HospitalsTab = ({ hospitals, reload, can }) => {
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 8) + "01");
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const summary = async (fn) => {
    try { const { data } = await api.get("/wholesale/allocation-summary", { params: { date_from: from, date_to: to } }); fn(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-4 shadow-sm">
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div><div className="text-xs text-muted-foreground">Summary from</div><Input type="date" className="w-40 h-9" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ws-summary-from" /></div>
        <div><div className="text-xs text-muted-foreground">to</div><Input type="date" className="w-40 h-9" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ws-summary-to" /></div>
        <Button variant="outline" onClick={() => summary(printSummary)} data-testid="ws-summary-print-all-btn"><Printer className="h-4 w-4 mr-1.5" /> Allocation summary (all)</Button>
        <Button variant="outline" onClick={() => summary(csvSummary)} data-testid="ws-summary-csv-btn"><Download className="h-4 w-4 mr-1.5" /> CSV</Button>
        {can("wholesale.accounts") && <Button className="ml-auto" data-testid="ws-new-hospital-btn" onClick={() => setForm({})}><Plus className="h-4 w-4 mr-1.5" /> New Hospital Partner</Button>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr><th className="py-2.5 px-3">Hospital Partner</th><th className="py-2.5 px-3">Contact</th><th className="py-2.5 px-3 text-right">Approved allocation</th>
              <th className="py-2.5 px-3 text-right">Used</th><th className="py-2.5 px-3 text-right">Committed</th><th className="py-2.5 px-3 text-right">Remaining</th><th className="py-2.5 px-3 w-40">Usage</th></tr>
          </thead>
          <tbody>
            {hospitals.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground"><Hospital className="h-8 w-8 mx-auto mb-2 opacity-40" />No Hospital Partners yet</td></tr>}
            {hospitals.map((h) => (
              <tr key={h.id} className="border-b border-border/60 hover:bg-muted/40 cursor-pointer" onClick={() => setDetail(h.id)} data-testid={`ws-hospital-row-${h.id}`}>
                <td className="py-2 px-3 font-medium">{h.name}{h.is_active === false && <span className="ml-2 text-[10px] rounded bg-muted px-1.5 py-0.5 text-muted-foreground">inactive</span>}</td>
                <td className="py-2 px-3 text-muted-foreground">{[h.contact_person, h.phone].filter(Boolean).join(" · ") || "—"}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(h.allocation)}</td>
                <td className="py-2 px-3 text-right font-mono" data-testid={`ws-hospital-used-${h.id}`}>{fmtGHS(h.used)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(h.committed)}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(h.remaining)}</td>
                <td className="py-2 px-3"><div className="flex items-center gap-2"><CreditBar pct={h.utilisation_pct} /><span className="text-xs font-mono w-12 text-right">{h.utilisation_pct}%</span></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <HospitalFormDialog open={!!form} hospital={form?.id ? form : null} onClose={() => setForm(null)} onSaved={reload} />
      <HospitalDetailDialog id={detail} can={can} from={from} to={to} onClose={() => setDetail(null)} onChanged={reload} onEdit={(h) => { setDetail(null); setForm(h); }} />
    </Card>
  );
};
